import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Lock, CheckCircle2, AlertTriangle, Activity, Database, Zap } from "lucide-react";
import { Card, Row, StatusPill } from "./_shared";
import { TestResultPanel } from "./EndpointsStep";

/**
 * Wizard Step 4 — Review & Publish.
 *
 * Renders the pre-publish checklist (≥1 endpoint, last test passed,
 * not retired) and gates the Publish button until all three pass. The
 * base-URL test runner here probes ``base_url`` with the configured
 * auth applied — same shape as the per-endpoint test in Step 3.
 *
 * Once published, the connection is immutable; any further change must
 * come via the "Clone to draft" button on the parent page.
 */
export default function PublishStep({
    conn, endpoints, usage, canTest, canPublish, onChanged,
}) {
    const [testing, setTesting] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [baseTestRun, setBaseTestRun] = useState(null);

    const testBase = async () => {
        setTesting(true); setBaseTestRun(null);
        try {
            const { data } = await api.post(`/admin/connections/${conn.id}/test/base`);
            setBaseTestRun(data);
            if (data.success) toast.success(`Base reachable — ${data.statusCode}`);
            else toast.warning(`Base test failed — ${(data.error?.errorType) || data.statusCode || "error"}`);
            onChanged();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setTesting(false);
        }
    };

    const publish = async () => {
        if (!window.confirm("Publish this connection? Once published it becomes immutable — clone to a draft to change anything.")) return;
        setPublishing(true);
        try {
            const { data } = await api.post(`/admin/connections/${conn.id}/publish`);
            toast.success(`Published v${data.version}`);
            onChanged();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setPublishing(false);
        }
    };

    const isPublished = conn.status === "published";
    const isRetired = conn.status === "retired";
    const hasEndpoints = endpoints.length > 0;
    const hasPassedTest = !!conn.last_test_success;
    const checks = [
        { label: "At least one endpoint defined", ok: hasEndpoints },
        { label: "Last connectivity test passed", ok: hasPassedTest },
        { label: "Status is draft (not retired)", ok: !isRetired },
    ];
    const canPublishNow = !isPublished && checks.every((c) => c.ok);

    return (
        <div className="space-y-4" data-testid="wizard-publish-step">
            <div className="grid md:grid-cols-2 gap-3">
                <Card title="Pre-publish checklist">
                    <ul className="space-y-1.5">
                        {checks.map((c) => (
                            <li key={c.label} className="flex items-center gap-2 text-xs"
                                data-testid={`publish-check-${c.ok ? "ok" : "fail"}`}>
                                {c.ok ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300" />
                                ) : (
                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-300" />
                                )}
                                <span className={c.ok ? "text-white/80" : "text-white/55"}>{c.label}</span>
                            </li>
                        ))}
                    </ul>
                </Card>
                <Card title="Usage">
                    <Row k="Flow versions using this" v={
                        <span className={usage.flow_count ? "text-violet-200 font-mono" : "text-white/55 font-mono"}
                            data-testid="publish-flow-count">
                            {usage.flow_count}
                        </span>
                    } />
                    <Row k="Endpoint count" v={endpoints.length} />
                    <Row k="Status" v={<StatusPill status={conn.status} />} />
                    <Row k="Version" v={`v${conn.version}`} />
                    {usage.flow_count > 0 && (
                        <div className="text-[10px] text-white/50 mt-2 border-t border-white/5 pt-2">
                            <Activity className="w-3 h-3 inline mr-1" />
                            This connection cannot be retired or deleted while flows depend on it.
                        </div>
                    )}
                </Card>
            </div>

            <div className="border border-white/10 rounded-md p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-sm">Connectivity test (base URL)</div>
                        <div className="text-[11px] text-white/55 mt-0.5">
                            Probes <span className="font-mono">{conn.base_url}</span> with the configured auth.
                            {conn.auth_type === "sitecentric_client_key" && " Obtains a session token with the tenant's key first and logs it out afterwards."}
                            {" "}A successful test is required to publish.
                        </div>
                    </div>
                    {canTest && (
                        <button onClick={testBase} disabled={testing}
                            data-testid="publish-test-base-btn"
                            className="px-3 py-1.5 text-xs border border-emerald-300/30 text-emerald-200 rounded-md hover:bg-emerald-300/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                            <Zap className="w-3 h-3" /> {testing ? "Running…" : "Run base test"}
                        </button>
                    )}
                </div>

                {baseTestRun && (
                    <TestResultPanel
                        run={{ endpoint: { method: "GET", relative_path: conn.base_url }, env: baseTestRun }}
                        onClose={() => setBaseTestRun(null)}
                    />
                )}
            </div>

            <div className="border border-white/10 rounded-md p-4 flex items-center justify-between gap-3 flex-wrap"
                data-testid="publish-action-row">
                <div>
                    <div className="text-sm inline-flex items-center gap-2">
                        <Lock className="w-4 h-4 opacity-70" /> Publish connection
                    </div>
                    <div className="text-[11px] text-white/55 mt-0.5">
                        Promotes <span className="font-mono">v{conn.version}</span> to <span className="font-mono">published</span>.
                        Published connections are immutable and consumable from Flow Builder.
                    </div>
                </div>
                {canPublish && (
                    isPublished ? (
                        <span className="text-xs text-emerald-200 inline-flex items-center gap-1.5"
                            data-testid="publish-already-published">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Already published
                        </span>
                    ) : (
                        <button onClick={publish} disabled={!canPublishNow || publishing}
                            data-testid="publish-btn"
                            className="px-4 py-2 text-xs bg-emerald-300 text-black rounded-md font-medium disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5" /> {publishing ? "Publishing…" : "Publish v" + conn.version}
                        </button>
                    )
                )}
            </div>

            {usage.flow_count > 0 && (
                <details className="border border-white/10 rounded-md p-3">
                    <summary className="text-xs text-white/70 cursor-pointer inline-flex items-center gap-1.5">
                        <Database className="w-3 h-3" /> Flow versions consuming this connection ({usage.flow_count})
                    </summary>
                    <table className="w-full text-xs mt-2">
                        <thead className="text-[10px] uppercase tracking-wider text-white/50">
                            <tr>
                                <th className="text-left px-2 py-1.5">Flow</th>
                                <th className="text-left px-2 py-1.5">Version</th>
                                <th className="text-left px-2 py-1.5">Status</th>
                                <th className="text-left px-2 py-1.5">Tenant</th>
                            </tr>
                        </thead>
                        <tbody>
                            {usage.flows.map((f) => (
                                <tr key={`${f.flow_id}-${f.version}`} className="border-t border-white/5">
                                    <td className="px-2 py-1.5">
                                        <Link to={`/flows/${f.flow_id}`} className="hover:underline">
                                            {f.name || f.flow_id}
                                        </Link>
                                    </td>
                                    <td className="px-2 py-1.5 font-mono">v{f.version}</td>
                                    <td className="px-2 py-1.5">{f.status}</td>
                                    <td className="px-2 py-1.5 font-mono text-white/55">{f.client_id}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}
        </div>
    );
}
